import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MessagesSquare, ChevronLeft, BellRing, Archive } from 'lucide-react';
import DispatchThreadPanel from './DispatchThreadPanel';
import { useAuth } from '../App';
import { permissionState, pushSupported } from '../lib/push';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' }) : '';

const MyStatusDot = ({ status }) => {
  if (!status || status === 'notified') return null;
  const cls = status === 'confirmed' ? 'bg-emerald-500' : status === 'declined' ? 'bg-red-500' : 'bg-amber-400 animate-pulse';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} title={status} />;
};

function ThreadRow({ t, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-slate-200 hover:bg-slate-50 transition-colors ${active ? 'bg-white' : ''} ${t.status === 'cancelled' ? 'opacity-60' : ''}`}
      data-testid={`chat-thread-row-${t.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-900 font-semibold text-sm truncate flex items-center gap-2">
          <MyStatusDot status={t.my_status} />{t.title}
        </span>
        <span className="text-[11px] text-slate-500 flex-shrink-0">{fmtWhen(t.last_message_at)}</span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <span className="text-xs text-slate-500 truncate">
          {t.date_label}{t.last_message_preview ? ` · ${t.last_sender_name ? t.last_sender_name.split(' ')[0] + ': ' : ''}${t.last_message_preview}` : ''}
        </span>
        {t.unread > 0 && <span className="flex-shrink-0 bg-blue-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`chat-unread-${t.id}`}>{t.unread}</span>}
      </div>
      {t.my_status === 'sent' && t.status !== 'cancelled' && (
        <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide text-amber-700" data-testid={`chat-needs-reply-${t.id}`}>Needs your ✓ / ✗</span>
      )}
    </button>
  );
}

export default function DispatchChat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeId = params.get('thread');
  const [threads, setThreads] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dispatch-chat/threads`, { ...auth(), params: { all: showAll ? 1 : 0 } });
      setThreads(r.data.threads || []);
    } catch { setThreads([]); }
  }, [showAll]);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  // Deep links from the request card / schedule bubble: ?request=<id> or ?wp=<id> → resolve to the thread
  const reqParam = params.get('request');
  const wpParam = params.get('wp');
  useEffect(() => {
    if (!reqParam && !wpParam) return;
    const url = reqParam ? `${API}/dispatch-chat/for-request/${reqParam}` : `${API}/dispatch-chat/for-work-period/${wpParam}`;
    axios.get(url, auth()).then((r) => {
      if (r.data?.thread_id) setParams({ thread: r.data.thread_id }, { replace: true });
      else { setParams({}, { replace: true }); toast.error('No job chat yet — post the dispatch first'); }
    }).catch(() => setParams({}, { replace: true }));
  }, [reqParam, wpParam, setParams]);

  const open = (id) => setParams(id ? { thread: id } : {});
  const pushOff = pushSupported() ? permissionState() !== 'granted' : true;

  return (
    <div className="h-[calc(100vh-7.5rem)] lg:h-[calc(100vh-8.5rem)] flex flex-col" data-testid="dispatch-chat-page">
      {pushOff && (
        <button onClick={() => navigate('/notifications')} className="mb-3 w-full flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs sm:text-sm px-3 py-2 rounded-xl hover:bg-amber-50 text-left" data-testid="chat-push-nudge">
          <BellRing className="w-4 h-4 flex-shrink-0" /> Notifications are off on this device — tap to turn them on so you don't miss a dispatch.
        </button>
      )}
      <div className="flex-1 min-h-0 flex rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {/* Thread list */}
        <div className={`${activeId ? 'hidden lg:flex' : 'flex'} w-full lg:w-96 flex-col border-r border-slate-200`}>
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-slate-900 font-bold flex items-center gap-2"><MessagesSquare className="w-5 h-5 text-blue-700" /> Dispatch Chat</h2>
            <button onClick={() => setShowAll((v) => !v)} className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg ${showAll ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-900'}`} data-testid="chat-show-all-toggle">
              <Archive className="w-3.5 h-3.5" /> {showAll ? 'Showing all' : 'Show past jobs'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="chat-thread-list">
            {threads === null && <p className="p-4 text-sm text-slate-500">Loading…</p>}
            {threads && threads.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="chat-empty">
                No job chats yet. When you're dispatched on a job, the conversation shows up here.
              </div>
            )}
            {(threads || []).map((t) => <ThreadRow key={t.id} t={t} active={t.id === activeId} onClick={() => open(t.id)} />)}
          </div>
        </div>
        {/* Thread */}
        <div className={`${activeId ? 'flex' : 'hidden lg:flex'} flex-1 min-w-0 flex-col`}>
          {activeId ? (
            <>
              <button onClick={() => open(null)} className="lg:hidden flex items-center gap-1 text-xs text-slate-700 px-3 py-2 border-b border-slate-200" data-testid="chat-back-btn">
                <ChevronLeft className="w-4 h-4" /> All chats
              </button>
              <div className="flex-1 min-h-0">
                <DispatchThreadPanel threadId={activeId} currentUser={user} onStatusChange={load} />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Pick a job on the left to open its chat.</div>
          )}
        </div>
      </div>
    </div>
  );
}
